import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  ThemeProvider,
  createTheme,
  useTheme as useMuiTheme,
} from '@mui/material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '../context/ThemeContext';

// Importando a fonte Montserrat via CSS
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';

// Importando hooks customizados
import useDashboard from '../hooks/useDashboard';
import useTimer from '../hooks/useTimer';
import useSocketManager from '../hooks/useSocketManager';
import useAtendimentoHandlers from '../hooks/useAtendimentoHandlers';
import useChamadaHandlers from '../hooks/useChamadaHandlers';
import useDashboardEffects from '../hooks/useDashboardEffects';

// Importando componentes customizados
import GuicheSelector from '../components/Dashboard/GuicheSelector';
import DashboardStats from '../components/Dashboard/DashboardStats';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import DashboardDialogs from '../components/Dashboard/DashboardDialogs';
import EmitirSenhaModal from '../components/Dashboard/EmitirSenhaModal';
import AdicionarServicosModal from '../components/Dashboard/AdicionarServicosModal';

// Criando um tema personalizado com a fonte Montserrat
const theme = createTheme({
  typography: {
    fontFamily: [
      'Montserrat',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: { fontFamily: 'Montserrat', fontWeight: 700 },
    h2: { fontFamily: 'Montserrat', fontWeight: 700 },
    h3: { fontFamily: 'Montserrat', fontWeight: 600 },
    h4: { fontFamily: 'Montserrat', fontWeight: 600 },
    h5: { fontFamily: 'Montserrat', fontWeight: 500 },
    h6: { fontFamily: 'Montserrat', fontWeight: 500 },
    button: { fontFamily: 'Montserrat', fontWeight: 500 },
  },
});

const Dashboard = () => {
  const baseTheme = useMuiTheme();
  const { isDarkMode, toggleTheme } = useTheme();
  
  // Combinando temas - mantendo cores e configurações existentes com a nova tipografia
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      ...theme.typography,
    },
  });

  // Hook principal do Dashboard
  const dashboardState = useDashboard();
  
  // Desestruturação dos estados e funções do dashboard
  const {
    // Estados principais
    sidebarOpen,
    setSidebarOpen,
    senhaChamada,
    setSenhaChamada,
    atendimentoIniciado,
    setAtendimentoIniciado,
    atendimentoFinalizado,
    setAtendimentoFinalizado,
    senhasProcessadas,
    setSenhasProcessadas,
    guicheSelecionado,
    setGuicheSelecionado,
    
    // Estados dos diálogos
    dialogoAberto,
    setDialogoAberto,
    dialogoReabrirSenhaAberto,
    setDialogoReabrirSenhaAberto,
    dialogoChamarSenhaEspecifica,
    setDialogoChamarSenhaEspecifica,
    dialogoAlterarServicoAberto,
    setDialogoAlterarServicoAberto,
    dialogoConfirmarAlteracaoAberto,
    setDialogoConfirmarAlteracaoAberto,
    modalEmitirSenhaAberto,
    setModalEmitirSenhaAberto,
    modalAdicionarServicosAberto,
    setModalAdicionarServicosAberto,
    acaoEscolhida,
    setAcaoEscolhida,
    atendenteEscolhido,
    setAtendenteEscolhido,
    senhaSelecionada,
    setSenhaSelecionada,
    
    // Estados da fila
    proximasSenhas,
    setProximasSenhas,
    carregando,
    erro,
    
    // Estados do timer
    tempoAtendimento,
    setTempoAtendimento,
    timerRef,
    
    // Estados de controle
    rechamando,
    setRechamando,
    chamadaEmAndamento,
    setChamadaEmAndamento,
    tamanhoDaFila,
    setTamanhoDaFila,
    aguardandoChamada,
    setAguardandoChamada,
    
    // Estados de estatísticas
    estatisticas,
    carregandoEstatisticas,
    atualizandoUsuario,
    
    // Estados do aviso de aguardo na fila
    aguardandoNaFila,
    setAguardandoNaFila,
    mensagemAguardo,
    setMensagemAguardo,
    
    // Dados computados
    senhasOrdenadas,
    
    // Funções
    formatarTempo,
    calcularTempoEspera,
    salvarEstadoAtendimento,
    limparEstadoAtendimento,
    buscarSenhaEmAtendimento,
    buscarSenhas,
    buscarEstatisticas,
    atualizarDadosUsuario,
    
    // Dependências
    user,
    updateUser,
    atualizarGuicheUsuario
  } = dashboardState;

  // Estado local para controlar atualização forçada do histórico
  const [forceHistoricoRefresh, setForceHistoricoRefresh] = useState(false);

  // Hook do timer
  const { pararTimer } = useTimer(atendimentoIniciado, setTempoAtendimento);

  // Hook do gerenciador de WebSocket (precisa vir antes do useChamadaHandlers)
  const { chamarSenhaViaSocket, rechamarSenhaViaSocket } = useSocketManager({
    user,
    buscarSenhas,
    buscarSenhaEmAtendimento,
    calcularTempoEspera,
    setSenhaChamada,
    salvarEstadoAtendimento,
    setChamadaEmAndamento,
    setTamanhoDaFila,
    setAguardandoChamada,
    aguardandoChamada,
    senhaSelecionada,
    senhasOrdenadas,
    handleConfirmarChamarSenhaEspecifica: null, // Será definido abaixo
    handleChamarProxima: null, // Será definido abaixo
    // Props para aviso simples de aguardo
    setAguardandoNaFila,
    setMensagemAguardo
  });

  // Hook dos handlers de chamada (agora pode usar chamarSenhaViaSocket)
  const {
    handleChamarProxima,
    handleConfirmarChamarSenhaEspecifica,
    handleGuicheChange
  } = useChamadaHandlers({
    senhaChamada,
    atendimentoFinalizado,
    chamadaEmAndamento,
    senhasOrdenadas,
    proximasSenhas,
    user,
    guicheSelecionado,
    senhaSelecionada,
    setSenhaChamada,
    setAtendimentoIniciado,
    setAtendimentoFinalizado,
    setTempoAtendimento,
    setProximasSenhas,
    setDialogoChamarSenhaEspecifica,
    setSenhaSelecionada,
    setAguardandoChamada,
    limparEstadoAtendimento,
    timerRef,
    chamarSenhaViaSocket, // Agora está definido
    atualizarGuicheUsuario,
    updateUser
  });

  // Hook dos handlers de atendimento
  const {
    handleIniciarAtendimento,
    handleFinalizarAtendimento,
    handleMostrarDialogoNaoCompareceu,
    handleConfirmarNaoCompareceu,
    handleCancelarNaoCompareceu,
    handleMostrarDialogoReabrirSenha,
    handleConfirmarReabrirSenha,
    handleCancelarReabrirSenha,
    handleReabrirSenha,
    handleRechamarSenha,
    handleClicarSenha,
    handleCancelarChamarSenhaEspecifica,
    handleEnviarParaAtendente,
    handleEscolherAtendente,
    handleMostrarDialogoAlterarServico,
    handleCancelarAlterarServico,
    handleNaoAlterarServico,
    handleSimAlterarServico,
    handleConfirmarAlteracaoComNovoServico,
    handleCancelarConfirmacaoAlteracao,
    executarVoltarParaFila,
    transferindo
  } = useAtendimentoHandlers({
    senhaChamada,
    atendimentoIniciado,
    atendimentoFinalizado,
    tempoAtendimento,
    senhasProcessadas,
    setSenhaChamada,
    setAtendimentoIniciado,
    setAtendimentoFinalizado,
    setTempoAtendimento,
    setSenhasProcessadas,
    setDialogoAberto,
    setDialogoReabrirSenhaAberto,
    setDialogoChamarSenhaEspecifica,
    setDialogoAlterarServicoAberto,
    setDialogoConfirmarAlteracaoAberto,
    setAcaoEscolhida,
    setAtendenteEscolhido,
    acaoEscolhida,
    atendenteEscolhido,
    setSenhaSelecionada,
    setRechamando,
    setProximasSenhas,
    proximasSenhas,
    limparEstadoAtendimento,
    salvarEstadoAtendimento,
    buscarSenhas,
    buscarEstatisticas,
    timerRef,
    user,
    guicheSelecionado,
    rechamarSenhaViaSocket
  });

  // Hook dos efeitos do Dashboard
  useDashboardEffects({
    user,
    guicheSelecionado,
    buscarSenhas,
    buscarEstatisticas,
    buscarSenhaEmAtendimento,
    atendimentoIniciado,
    senhaChamada,
    atendimentoFinalizado,
    tempoAtendimento,
    salvarEstadoAtendimento,
    setGuicheSelecionado
  });

  // Função para abrir o modal de emitir senha
  const handleEmitirSenha = () => {
    setModalEmitirSenhaAberto(true);
  };

  // Função para fechar o modal de emitir senha
  const handleFecharModalEmitirSenha = () => {
    setModalEmitirSenhaAberto(false);
  };

  // Callback para quando uma senha for emitida
  const handleSenhaEmitida = async (senhaData) => {
    try {
      // 1. Atualizar a fila de senhas
      await buscarSenhas();
      
      // 2. Buscar a senha em atendimento do usuário (para pegar a senha recém-emitida)
      await buscarSenhaEmAtendimento();
      
      // 3. Atualizar estatísticas
      await buscarEstatisticas();
      
      console.log('✅ Dashboard atualizado após emissão de senha:', senhaData);
    } catch (error) {
      console.error('❌ Erro ao atualizar dashboard após emissão:', error);
    }
  };

  // Função para abrir o modal de adicionar serviços
  const handleAdicionarServicos = () => {
    setModalAdicionarServicosAberto(true);
  };

  // Função para fechar o modal de adicionar serviços
  const handleFecharModalAdicionarServicos = () => {
    setModalAdicionarServicosAberto(false);
  };

  // Callback para quando serviços forem adicionados
  const handleServicosAdicionados = async (servicosAdicionados) => {
    try {
      // Atualizar dados relevantes
      await buscarSenhas();
      await buscarEstatisticas();
      await buscarSenhaEmAtendimento(); // Atualizar senha atual para mostrar novos serviços
      
      // Forçar atualização do histórico
      setForceHistoricoRefresh(prev => !prev);
      
      console.log('✅ Dashboard atualizado após adição de serviços:', servicosAdicionados);
    } catch (error) {
      console.error('❌ Erro ao atualizar dashboard após adição de serviços:', error);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar sidebarOpen={sidebarOpen} />

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Header 
            sidebarOpen={sidebarOpen} 
            handleSidebarToggle={dashboardState.toggleSidebar}
          />

          <Container 
            maxWidth="xxl" 
            sx={{ 
              mt: { xs: 8, sm: 9, md: 10 }, 
              mb: { xs: 2, sm: 3, md: 4 },
              px: { 
                xs: sidebarOpen ? 1 : 2, 
                sm: sidebarOpen ? 2 : 3,
                md: sidebarOpen ? 2.5 : 4,
                lg: sidebarOpen ? 3 : 4
              }
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { 
                xs: 'column', 
                sm: sidebarOpen ? 'column' : 'row',
                md: 'row' 
              },
              justifyContent: 'space-between', 
              alignItems: { 
                xs: 'flex-start', 
                sm: sidebarOpen ? 'flex-start' : 'center',
                md: 'center' 
              },
              gap: { xs: 2, sm: sidebarOpen ? 2 : 1, md: 0 },
              mb: { xs: 2.5, sm: 3, md: 3 }
            }}>
              <Typography 
                variant="h4" 
                fontWeight="bold"
                sx={{
                  fontSize: { 
                    xs: sidebarOpen ? '1.5rem' : '1.75rem',
                    sm: sidebarOpen ? '1.75rem' : '2rem', 
                    md: sidebarOpen ? '1.875rem' : '2.125rem',
                    lg: '2.125rem'
                  },
                  lineHeight: 1.2
                }}
              >
                Gerenciamento de Senhas
              </Typography>
              
              <GuicheSelector
                guicheSelecionado={guicheSelecionado}
                handleGuicheChange={handleGuicheChange}
                customTheme={customTheme}
                sidebarOpen={sidebarOpen}
              />
            </Box>
            
            {/* Cards de estatísticas */}
            <DashboardStats
              estatisticas={estatisticas}
              carregandoEstatisticas={carregandoEstatisticas}
              customTheme={customTheme}
              atualizarDadosUsuario={atualizarDadosUsuario}
              atualizandoUsuario={atualizandoUsuario}
              senhasNaFilaAtendente={senhasOrdenadas?.length || 0}
              sidebarOpen={sidebarOpen}
              onEmitirSenha={handleEmitirSenha}
              senhaChamada={senhaChamada}
              atendimentoIniciado={atendimentoIniciado}
              atendimentoFinalizado={atendimentoFinalizado}
            />
            
            {/* Layout principal com painéis */}
            <DashboardLayout
              // Props para SenhaAtualPanel
              senhaChamada={senhaChamada}
              atendimentoIniciado={atendimentoIniciado}
              atendimentoFinalizado={atendimentoFinalizado}
              tempoAtendimento={tempoAtendimento}
              rechamando={rechamando}
              formatarTempo={formatarTempo}
              handleIniciarAtendimento={handleIniciarAtendimento}
              handleFinalizarAtendimento={handleFinalizarAtendimento}
              handleMostrarDialogoNaoCompareceu={handleMostrarDialogoNaoCompareceu}
              handleReabrirSenha={handleReabrirSenha}
              handleRechamarSenha={handleRechamarSenha}
              handleAdicionarServicos={handleAdicionarServicos}
              
              // Props do aviso de aguardo na fila
              aguardandoNaFila={aguardandoNaFila}
              mensagemAguardo={mensagemAguardo}
              
              // Props para FilaPasswordsList
              senhasOrdenadas={senhasOrdenadas}
              handleClicarSenha={handleClicarSenha}
              handleChamarProxima={handleChamarProxima}
              carregando={carregando}
              chamadaEmAndamento={chamadaEmAndamento}
              aguardandoChamada={aguardandoChamada}
              
              // Props para HistoricoSenhas
              senhasProcessadas={senhasProcessadas}
              user={user}
              forceHistoricoRefresh={forceHistoricoRefresh}
              
              // Props gerais
              customTheme={customTheme}
              sidebarOpen={sidebarOpen}
            />
          </Container>
        </Box>

        {/* Diálogos */}
        <DashboardDialogs
          // Estados dos diálogos
          dialogoAberto={dialogoAberto}
          dialogoReabrirSenhaAberto={dialogoReabrirSenhaAberto}
          dialogoChamarSenhaEspecifica={dialogoChamarSenhaEspecifica}
          dialogoAlterarServicoAberto={dialogoAlterarServicoAberto}
          dialogoConfirmarAlteracaoAberto={dialogoConfirmarAlteracaoAberto}
          
          // Dados
          senhaChamada={senhaChamada}
          senhaSelecionada={senhaSelecionada}
          acaoEscolhida={acaoEscolhida}
          atendenteEscolhido={atendenteEscolhido}
          customTheme={customTheme}
          
          // Handlers dos diálogos
          handleCancelarNaoCompareceu={handleCancelarNaoCompareceu}
          handleConfirmarNaoCompareceu={handleConfirmarNaoCompareceu}
          handleCancelarReabrirSenha={handleCancelarReabrirSenha}
          handleConfirmarReabrirSenha={handleConfirmarReabrirSenha}
          handleCancelarChamarSenhaEspecifica={handleCancelarChamarSenhaEspecifica}
          handleConfirmarChamarSenhaEspecifica={handleConfirmarChamarSenhaEspecifica}
          handleEnviarParaAtendente={handleEnviarParaAtendente}
          handleEscolherAtendente={handleEscolherAtendente}
          handleMostrarDialogoAlterarServico={handleMostrarDialogoAlterarServico}
          handleCancelarAlterarServico={handleCancelarAlterarServico}
          handleNaoAlterarServico={handleNaoAlterarServico}
          handleSimAlterarServico={handleSimAlterarServico}
          handleConfirmarAlteracaoComNovoServico={handleConfirmarAlteracaoComNovoServico}
          handleCancelarConfirmacaoAlteracao={handleCancelarConfirmacaoAlteracao}
          buscarSenhas={buscarSenhas}
        />

        {/* Modal de Emitir Senha */}
        <EmitirSenhaModal
          open={modalEmitirSenhaAberto}
          onClose={handleFecharModalEmitirSenha}
          customTheme={customTheme}
          user={user}
          onSenhaEmitida={handleSenhaEmitida}
        />

        {/* Modal de Adicionar Serviços */}
        <AdicionarServicosModal
          open={modalAdicionarServicosAberto}
          onClose={handleFecharModalAdicionarServicos}
          senha={senhaChamada}
          customTheme={customTheme}
          onServicosAdicionados={handleServicosAdicionados}
        />

        <ToastContainer position="bottom-right" />
      </Box>
    </ThemeProvider>
  );
};

export default Dashboard; 